import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE } from "@/utils/languageRouter";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { toast } from "sonner";
import {
  Calendar,
  MapPin,
  Users,
  Clock,
  Mail,
  Phone,
  Globe,
  ChevronLeft,
  Check,
  ArrowRight,
  Loader2,
  User,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StageDetailPage = () => {
  const { slug, lang: urlLang } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const lang = urlLang || i18n.language;
  const currentLang = SUPPORTED_LANGUAGES.includes(lang) ? lang : DEFAULT_LANGUAGE;
  
  const localizedLink = (path) => {
    const cleanPath = path.startsWith("/") ? path : `/${path}`;
    return `/${currentLang}${cleanPath}`;
  };
  
  const [stage, setStage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [bookingForm, setBookingForm] = useState({
    user_name: "",
    user_email: "",
    user_phone: "",
    num_participants: 1,
    special_requests: "",
  });

  useEffect(() => {
    fetchStage();
  }, [slug]);

  const fetchStage = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/stages/${slug}`);
      setStage(res.data);
    } catch (err) {
      console.error("Error fetching stage:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    if (!bookingForm.user_name || !bookingForm.user_email || !bookingForm.user_phone) {
      toast.error(lang === "fr" ? "Veuillez remplir tous les champs obligatoires" : "Please fill in all required fields");
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/stage-reservations`, {
        stage_id: stage.id,
        ...bookingForm,
      });

      toast.success(lang === "fr" ? "Inscription confirmée ! Un email de confirmation vous a été envoyé." : "Registration confirmed! A confirmation email has been sent to you.");
      setShowBookingModal(false);

      // Redirect to payment
      const checkoutRes = await axios.post(`${API}/stage-reservations/${response.data.id}/checkout`);
      window.location.href = checkoutRes.data.checkout_url;
    } catch (error) {
      const message = error.response?.data?.detail || (lang === "fr" ? "Erreur lors de l'inscription" : "Registration error");
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!stage) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center pt-20">
        <h2 className="text-2xl font-semibold mb-4">Stage non trouvé</h2>
        <Link to={localizedLink("/stages")}>
          <Button>{t("common.back")}</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-20" data-testid="stage-detail-page">
      {/* Hero Image */}
      <div className="relative h-[50vh] md:h-[60vh]">
        <img
          src={stage.image_url || "https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=1200"}
          alt={stage.title?.[lang] || stage.title?.fr}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
        
        {/* Back Button */}
        <Link
          to={localizedLink("/stages")}
          className="absolute top-6 left-6 z-10"
        >
          <Button variant="secondary" size="sm" className="gap-2 rounded-full">
            <ChevronLeft className="h-4 w-4" />
            {t("common.back")}
          </Button>
        </Link>

        {/* Title Overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12">
          <div className="max-w-5xl mx-auto">
            <Badge className="mb-4 bg-white/20 text-white border-white/30">
              {stage.category}
            </Badge>
            <h1 className="font-serif text-3xl md:text-5xl font-bold text-white mb-4">
              {stage.title?.[lang] || stage.title?.fr}
            </h1>
            <div className="flex flex-wrap gap-6 text-white/90">
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                <span>{stage.duration_days} {t("stages.duration")}</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                <span>{stage.max_participants} {t("stages.maxParticipants")}</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                <span>{stage.location}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-12">
        <div className="grid lg:grid-cols-3 gap-12">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-12">
            {/* Description */}
            <section>
              <h2 className="font-serif text-2xl font-semibold mb-4">À propos</h2>
              <div className="prose prose-lg max-w-none">
                <p className="text-muted-foreground leading-relaxed">
                  {stage.description?.[lang] || stage.description?.fr}
                </p>
              </div>
            </section>

            {/* Program */}
            {stage.program && stage.program.length > 0 && (
              <section>
                <h2 className="font-serif text-2xl font-semibold mb-6">{t("stages.program")}</h2>
                <div className="space-y-6">
                  {stage.program.map((day, idx) => (
                    <div
                      key={idx}
                      className="flex gap-4 p-6 bg-muted/30 rounded-2xl"
                    >
                      <div className="flex-shrink-0 w-16 h-16 bg-primary/10 rounded-xl flex flex-col items-center justify-center">
                        <span className="text-xs text-primary font-medium">Jour</span>
                        <span className="text-2xl font-bold text-primary">{day.day || idx + 1}</span>
                      </div>
                      <div>
                        <h4 className="font-semibold mb-1">
                          {day.title?.[lang] || day.title?.fr || `Jour ${idx + 1}`}
                        </h4>
                        <p className="text-muted-foreground text-sm">
                          {day.description?.[lang] || day.description?.fr}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Includes */}
            {stage.includes && (
              <section>
                <h2 className="font-serif text-2xl font-semibold mb-6">{t("stages.includes")}</h2>
                <div className="grid md:grid-cols-2 gap-3">
                  {(stage.includes?.[lang] || stage.includes?.fr || []).map((item, idx) => (
                    <div
                      key={idx}
                      className="flex items-center gap-3 p-3 bg-green-50 rounded-xl"
                    >
                      <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                        <Check className="h-4 w-4 text-white" />
                      </div>
                      <span className="text-sm">{item}</span>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Gallery */}
            {stage.gallery && stage.gallery.length > 0 && (
              <section>
                <h2 className="font-serif text-2xl font-semibold mb-6">{t("gallery.title")}</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {stage.gallery.map((img, idx) => (
                    <img
                      key={idx}
                      src={img}
                      alt={`Gallery ${idx + 1}`}
                      className="aspect-square object-cover rounded-xl"
                    />
                  ))}
                </div>
              </section>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-6">
              {/* Price Card */}
              <div className="bg-card border border-border rounded-3xl p-6">
                <div className="text-center mb-6">
                  <span className="text-sm text-muted-foreground">{t("stages.from")}</span>
                  <p className="font-serif text-4xl font-bold text-primary">
                    {stage.price}€
                  </p>
                  <span className="text-sm text-muted-foreground">{t("stages.perPerson")}</span>
                </div>

                <Button 
                  className="w-full rounded-full mb-4" 
                  size="lg" 
                  data-testid="stage-book-btn"
                  onClick={() => setShowBookingModal(true)}
                >
                  {t("stages.bookNow")}
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>

                <a
                  href={`mailto:${stage.contact_email}`}
                  className="block"
                >
                  <Button variant="outline" className="w-full rounded-full" size="lg">
                    {t("stages.contact")}
                  </Button>
                </a>
              </div>

              {/* Organizer Card */}
              <div className="bg-muted/30 rounded-3xl p-6">
                <h3 className="font-semibold mb-4">{t("stages.organizedBy")}</h3>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                    <span className="text-primary font-bold text-lg">
                      {stage.collaborator_name?.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium">{stage.collaborator_name}</p>
                  </div>
                </div>
                <div className="space-y-3 text-sm">
                  <div className="flex items-center gap-3 text-muted-foreground">
                    <Mail className="h-4 w-4" />
                    <span>{stage.contact_email}</span>
                  </div>
                  {stage.contact_phone && (
                    <div className="flex items-center gap-3 text-muted-foreground">
                      <Phone className="h-4 w-4" />
                      <span>{stage.contact_phone}</span>
                    </div>
                  )}
                  {stage.website && (
                    <a
                      href={stage.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 text-primary hover:underline"
                    >
                      <Globe className="h-4 w-4" />
                      <span>Site web</span>
                    </a>
                  )}
                </div>
              </div>

              {/* Next Dates */}
              {stage.next_dates && stage.next_dates.length > 0 && (
                <div className="bg-muted/30 rounded-3xl p-6">
                  <h3 className="font-semibold mb-4">{t("stages.nextDates")}</h3>
                  <div className="space-y-3">
                    {stage.next_dates.map((date, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between p-3 bg-background rounded-xl"
                      >
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-primary" />
                          <span className="text-sm">
                            {date.start_date} - {date.end_date}
                          </span>
                        </div>
                        <Badge variant="secondary">
                          {date.available_spots} places
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Booking Modal */}
      <Dialog open={showBookingModal} onOpenChange={setShowBookingModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">
              {lang === "fr" ? "Réserver ce stage" : "Book this retreat"}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleBookingSubmit} className="space-y-4">
            <div>
              <Label htmlFor="user_name" className="flex items-center gap-2">
                <User className="h-4 w-4" />
                {lang === "fr" ? "Nom complet" : "Full name"} *
              </Label>
              <Input
                id="user_name"
                value={bookingForm.user_name}
                onChange={(e) => setBookingForm({ ...bookingForm, user_name: e.target.value })}
                placeholder="Jean Dupont"
                required
                data-testid="booking-name-input"
              />
            </div>

            <div>
              <Label htmlFor="user_email" className="flex items-center gap-2">
                <Mail className="h-4 w-4" />
                Email *
              </Label>
              <Input
                id="user_email"
                type="email"
                value={bookingForm.user_email}
                onChange={(e) => setBookingForm({ ...bookingForm, user_email: e.target.value })}
                placeholder="jean@example.com"
                required
                data-testid="booking-email-input"
              />
            </div>

            <div>
              <Label htmlFor="user_phone" className="flex items-center gap-2">
                <Phone className="h-4 w-4" />
                {lang === "fr" ? "Téléphone" : "Phone"} *
              </Label>
              <Input
                id="user_phone"
                type="tel"
                value={bookingForm.user_phone}
                onChange={(e) => setBookingForm({ ...bookingForm, user_phone: e.target.value })}
                placeholder="+212 6XX XXX XXX"
                required
                data-testid="booking-phone-input"
              />
            </div>

            <div>
              <Label htmlFor="num_participants" className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                {lang === "fr" ? "Nombre de participants" : "Number of participants"}
              </Label>
              <Input
                id="num_participants"
                type="number"
                min="1"
                max={stage?.max_participants || 20}
                value={bookingForm.num_participants}
                onChange={(e) => setBookingForm({ ...bookingForm, num_participants: parseInt(e.target.value) || 1 })}
                data-testid="booking-participants-input"
              />
            </div>

            <div>
              <Label htmlFor="special_requests">
                {lang === "fr" ? "Demandes spéciales (optionnel)" : "Special requests (optional)"}
              </Label>
              <Textarea
                id="special_requests"
                value={bookingForm.special_requests}
                onChange={(e) => setBookingForm({ ...bookingForm, special_requests: e.target.value })}
                placeholder={lang === "fr" ? "Régime alimentaire, besoins spécifiques..." : "Dietary requirements, specific needs..."}
                rows={3}
              />
            </div>

            <div className="bg-muted/50 p-4 rounded-xl">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">{lang === "fr" ? "Prix par personne" : "Price per person"}</span>
                <span className="font-semibold">{stage?.price}€</span>
              </div>
              <div className="flex justify-between items-center mt-2 pt-2 border-t">
                <span className="font-semibold">{lang === "fr" ? "Total" : "Total"}</span>
                <span className="text-xl font-bold text-primary">
                  {(stage?.price || 0) * bookingForm.num_participants}€
                </span>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => setShowBookingModal(false)}
              >
                {lang === "fr" ? "Annuler" : "Cancel"}
              </Button>
              <Button
                type="submit"
                className="flex-1"
                disabled={submitting}
                data-testid="booking-submit-btn"
              >
                {submitting ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : null}
                {lang === "fr" ? "Confirmer et payer" : "Confirm and pay"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StageDetailPage;
